<script setup lang="ts">
import { vIntersectionObserver } from "@vueuse/components";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VInlineButton from "@magnit/core/src/components/VInlineButton/VInlineButton.vue";
import VStack from "@magnit/core/src/components/VStack/VStack.vue";
import VStackItem from "@magnit/core/src/components/VStack/VStackItem.vue";
import VWrapper from "@magnit/core/src/components/VWrapper/VWrapper.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VNavigationSubmenu from "@magnit/unit-navigation/src/VNavigationSubmenu.vue";
import type {
  ISubMenu,
  ISubMenuLink,
} from "@magnit/unit-navigation/src/VNavigation.types";
import { useAnalyticManager } from "@magnit/analytic-events";
import VFooterApps from "./VFooterApps.vue";
import VFooterSocials from "./VFooterSocials.vue";
import VFooterContacts from "./VFooterContacts.vue";
import type { IFooterProps } from "./footer.types";
import { linkSecurity, socials as socialsDefault } from "./footer.vars";

withDefaults(defineProps<IFooterProps>(), {
  project: "magnit",
  socials: () => socialsDefault,
});

// Analytics
const { send } = useAnalyticManager();

const onVisibilityNavigation = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    send("Footer:Links:View");
  }
};
const onToggleSubmenuNavigation = (item: ISubMenu) => {
  send("Footer:Links:More:Click", {
    group: item.title,
  });
};
const onClickLinkNavigation = (item: ISubMenuLink) => {
  send("Footer:Links:Item:Click", {
    link_text: item.label,
    link_dest: item.url,
  });
};

const handleViewSecurityBlock = ([
  { isIntersecting },
]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    send("Footer:Security:View");
  }
};
const handleClickSecurityBtn = () => {
  send("Footer:Security:Click");
};
</script>

<template>
  <footer
    class="unit-footer"
    :class="{ 'unit-footer_no-links': !links.length }"
  >
    <hr class="unit-footer-divider" />
    <VWrapper
      :indent="{ xs: '12px', s: '20px', m: '32px' }"
      class="unit-footer-wrapper-info"
    >
      <section class="unit-footer-section-apps">
        <VFooterApps :project="project" />
      </section>
      <section v-if="links.length" class="unit-footer-section-nav">
        <VNavigationSubmenu
          v-intersection-observer="onVisibilityNavigation"
          class="unit-footer-links"
          :menu="links"
          @submenu-open="onToggleSubmenuNavigation"
          @submenu-close="onToggleSubmenuNavigation"
          @link-click="onClickLinkNavigation"
        >
          <template #before-links>
            <VHidden
              :hide="{ xs: false, ml: true }"
              class="unit-footer-socials unit-footer-socials_before-links"
            >
              <VFooterSocials :links="socials" />
            </VHidden>
          </template>
          <template #after-links>
            <VHidden
              :hide="{ xs: true, ml: false }"
              class="unit-footer-socials unit-footer-socials_after-links"
            >
              <VFooterSocials :links="socials" />
            </VHidden>
          </template>
        </VNavigationSubmenu>
      </section>
    </VWrapper>
    <section class="unit-footer-section-contacts">
      <hr class="unit-footer-divider" />

      <VWrapper :indent="{ xs: '12px', s: '20px', m: '32px' }">
        <VStack
          :gap="{ xs: '8px', s: '12px', m: '20px', l: '0px', xl: '24px' }"
          direction="row"
          wrap="nowrap"
        >
          <VStackItem :size="{ xs: 12, l: 7, xl: 8 }">
            <VFooterContacts />
            <div
              v-intersection-observer="handleViewSecurityBlock"
              class="unit-footer-security-form"
            >
              <VText font="body-small-regular"
                >Нашли уязвимость на сайте?
              </VText>
              <a
                :href="linkSecurity"
                target="_blank"
                rel="noreferrer"
                @click="handleClickSecurityBtn"
              >
                <VInlineButton tag="span" theme="secondary">
                  Заполните форму
                </VInlineButton>
              </a>
            </div>
          </VStackItem>
        </VStack>
      </VWrapper>

      <VHidden :hide="{ xs: true, ml: false }">
        <hr class="unit-footer-divider" />
      </VHidden>

      <VWrapper :indent="{ xs: '12px', s: '20px', m: '32px' }">
        <VText
          tag="div"
          class="unit-footer-copyright"
          font="caption-large-regular"
          color="secondary"
        >
          <span>© Официальный сайт сети «Магнит».</span>
          <span>2010-{{ new Date().getFullYear() }} АО «Тандер»</span>
        </VText>
      </VWrapper>
    </section>
  </footer>
</template>

<style lang="postcss" scoped>
.unit-footer {
  display: flex;
  flex-direction: column;

  & > * {
    width: 100%;
  }

  & a {
    text-decoration: none;
  }

  & .unit-footer-links {
    position: static;
    padding: var(--pl-unit-x2) 0 0 0;

    @media (--pl-viewport-from-l) {
      padding: var(--pl-unit-x2) 0 var(--pl-unit-x8) 0;
    }

    :deep(.pl-navigation-submenu__links) {
      padding-bottom: 0;
    }
  }

  &-wrapper-info {
    display: flex;
    flex-direction: column;
  }

  :deep(.pl-navigation-submenu__overlay) {
    display: none;
  }

  :deep(.pl-navigation-submenu__container .pl-wrapper) {
    padding: 0;
  }
}

.unit-footer-section-apps {
  .unit-footer_no-links & {
    padding-bottom: var(--pl-unit-x6);
  }
}

.unit-footer-socials {
  &_before-links {
    margin-bottom: var(--pl-unit-x6);
  }

  &_after-links {
    :deep(.unit-footer-socials__links) {
      @media (--pl-viewport-from-ml) {
        width: 112px;
      }

      @media (--pl-viewport-from-l) {
        width: auto;
      }
    }
  }
}

.unit-footer-section-contacts {
  order: 3;
  background-color: var(--pl-surface-muted-default);
}

.unit-footer-divider {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  color: transparent;
  border: none;
  border-top: 1px solid var(--pl-divider-primary);
}

.unit-footer-copyright {
  padding: var(--pl-unit-x4) 0 var(--pl-unit-x8) 0;
  display: flex;
  flex-direction: column;
}

.unit-footer-security-form {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

@media (--pl-viewport-from-ml) {
  .unit-footer-copyright {
    padding: var(--pl-unit-x6) 0 var(--pl-unit-x6) 0;
    flex-direction: row;
    gap: 0.5em;
  }

  .unit-footer-security-form {
    gap: var(--pl-unit-x1);
    margin-bottom: var(--pl-unit-x6);
  }

  :deep(.pl-navigation-submenu__links) {
    column-count: 4;
  }
}

@media (--pl-viewport-from-l) {
  .unit-footer {
    flex-flow: row wrap;

    &-wrapper-info {
      flex-direction: row;
      gap: var(--pl-unit-x5);
    }
  }

  .unit-footer-section-apps {
    width: 50%;

    .unit-footer_no-links & {
      padding-bottom: 0;
    }

    .unit-footer & {
      width: 50%;
    }
  }

  .unit-footer-section-nav {
    .unit-footer & {
      width: 50%;
    }
  }

  :deep(.pl-navigation-submenu__links) {
    column-count: 2;
  }
}
</style>
